<template>
    <e-sidebar
        class="podcast-config-drawer"
        :model-value="open"
        right
        overlay
        :can-cancel="['outside', 'escape']"
        fullheight
        @update:modelValue="$emit('update:open', $event)"
    >
        <div class="podcast-drawer-shell tw:h-full tw:min-h-0 tw:flex tw:flex-col">
            <div class="tw:flex tw:items-center tw:justify-between tw:p-4 tw:border-b tw:border-gray-200 tw:bg-white">
                <h3 class="tw:text-base tw:font-semibold">Podcast Configuration</h3>
                <button type="button" class="delete" @click="$emit('update:open', false)" />
            </div>

            <div class="tw:flex-1 tw:min-h-0 tw:overflow-y-auto tw:flex tw:flex-col">
                <div class="tw:p-4 tw:flex tw:flex-col tw:gap-3">
                    <div
                        v-if="isLoading"
                        class="tw:flex tw:items-center tw:justify-center tw:py-10 tw:text-sm tw:text-gray-500"
                    >
                        Loading podcast configuration options...
                    </div>

                    <e-message
                        v-else-if="loadError"
                        type="is-danger"
                        :closable="false"
                    >
                        <div class="tw:flex tw:items-center tw:justify-between tw:gap-3">
                            <span>{{ loadError }}</span>
                            <e-button size="is-small" outlined @click="loadConfiguration">
                                Retry
                            </e-button>
                        </div>
                    </e-message>

                    <template v-else>
                        <div v-if="filteredCategories.length > 0" class="tw:flex tw:flex-col tw:gap-3">
                            <div
                                v-for="category in filteredCategories"
                                :key="category.key"
                                class="tw:border tw:border-gray-200 tw:rounded-lg tw:bg-white tw:p-3"
                            >
                                <p class="tw:text-xs tw:uppercase tw:tracking-wide tw:text-gray-500 tw:mb-2">
                                    {{ category.label }}
                                </p>
                                <div
                                    v-for="subcategory in category.subcategories || []"
                                    :key="`${category.key}-${subcategory.key}`"
                                    class="tw:mb-3 last:tw:mb-0"
                                >
                                    <p class="tw:text-sm tw:font-semibold tw:mb-1">{{ subcategory.label }}</p>
                                    <p
                                        v-if="subcategory.description"
                                        class="tw:text-xs tw:text-gray-500 tw:mb-2"
                                    >
                                        {{ subcategory.description }}
                                    </p>
                                    <p
                                        v-else-if="getSelectionMode(subcategory) === 'multiple'"
                                        class="tw:text-xs tw:text-gray-500 tw:mb-2"
                                    >
                                        Choose up to {{ getMaxSelections(subcategory) }} voices
                                    </p>
                                    <template v-if="getSelectionMode(subcategory) === 'single'">
                                        <e-select
                                            :model-value="String(getSelectedOption(category.key, subcategory.key) || '')"
                                            :options="subcategory.options || []"
                                            option-label="label"
                                            option-value="key"
                                            :disabled="readonly"
                                            placeholder="Select one option"
                                            @update:modelValue="handleSingleSelectModel(category.key, subcategory, $event)"
                                        />
                                    </template>
                                    <template v-else>
                                        <div class="tw:flex tw:flex-col tw:gap-2">
                                            <label
                                                v-for="option in subcategory.options || []"
                                                :key="option.key"
                                                class="tw:flex tw:items-start tw:gap-2 tw:text-sm tw:text-gray-700"
                                            >
                                                <input
                                                    type="checkbox"
                                                    :checked="isOptionSelected(category.key, subcategory, option.key)"
                                                    :disabled="readonly || isOptionDisabled(category.key, subcategory, option.key)"
                                                    @change="selectOption(category.key, subcategory, option.key)"
                                                >
                                                <span>{{ option.label }}</span>
                                            </label>
                                        </div>
                                    </template>
                                </div>
                            </div>
                        </div>
                        <e-message v-else type="is-info" :closable="false">
                            {{ hasCategories ? 'No configuration options available.' : 'No configuration options are available right now.' }}
                        </e-message>
                    </template>
                </div>
            </div>

            <div class="tw:p-4 tw:border-t tw:border-gray-200 tw:bg-white">
                <p class="tw:text-xs tw:uppercase tw:tracking-wide tw:text-gray-500 tw:mb-2">Preview</p>
                <div class="tw:text-sm tw:text-gray-700 tw:mb-3 tw:min-h-[40px]">
                    {{ previewText || 'No options selected yet.' }}
                </div>
                <div class="tw:flex tw:items-center tw:justify-between tw:gap-2">
                    <span class="tw:text-sm tw:text-gray-600">{{ selectedCount }} options selected</span>
                    <div class="tw:flex tw:items-center tw:gap-2">
                        <e-button
                            v-if="showApplyToAll && !readonly"
                            size="is-small"
                            outlined
                            @click="$emit('apply-to-all', buildPayload())"
                        >
                            Apply to All
                        </e-button>
                        <e-button
                            size="is-small"
                            outlined
                            :disabled="readonly"
                            @click="resetSelections"
                        >
                            Reset
                        </e-button>
                        <e-button
                            size="is-small"
                            type="is-primary"
                            :disabled="readonly"
                            @click="applyConfig"
                        >
                            Apply Config
                        </e-button>
                    </div>
                </div>
            </div>
        </div>
    </e-sidebar>
</template>

<script>
import {
    buildCommaSeparatedTextFromSelections,
    normalizeVoiceCastSelectionKeys,
    usePodcastConfiguration,
} from '@/composables/usePodcastConfiguration.js';

export default {
    name: 'PodcastConfigurationDrawer',

    props: {
        open: {
            type: Boolean,
            default: false,
        },
        modelValue: {
            type: Object,
            default: () => ({}),
        },
        readonly: {
            type: Boolean,
            default: false,
        },
        showApplyToAll: {
            type: Boolean,
            default: false,
        },
    },

    emits: ['update:open', 'update:modelValue', 'apply', 'apply-to-all'],

    data() {
        return {
            localConfig: {
                selections: {},
            },
            resolvedConfigState: {
                categories: [],
                optionsMeta: {},
                loading: false,
                error: null,
            },
        };
    },

    computed: {
        localSelections() {
            return this.localConfig?.selections || {};
        },

        selectedCount() {
            return Object.values(this.localSelections).reduce((count, subcategories) => {
                if (!subcategories || typeof subcategories !== 'object') {
                    return count;
                }

                return (
                    count +
                    Object.values(subcategories).reduce((subCount, selected) => {
                        if (Array.isArray(selected)) return subCount + selected.length;
                        if (typeof selected === 'string' && selected !== '') return subCount + 1;
                        return subCount;
                    }, 0)
                );
            }, 0);
        },

        previewText() {
            return buildCommaSeparatedTextFromSelections(this.localSelections, this.categories);
        },

        categories() {
            return this.resolvedConfigState.categories;
        },

        filteredCategories() {
            return this.categories;
        },

        isLoading() {
            return this.resolvedConfigState.loading === true;
        },

        loadError() {
            return this.resolvedConfigState.error;
        },

        hasCategories() {
            return this.categories.length > 0;
        },

        optionsMeta() {
            return this.resolvedConfigState.optionsMeta || {};
        },
    },

    watch: {
        modelValue: {
            immediate: true,
            deep: true,
            handler(value) {
                this.localConfig = this.normalizeFromModelValue(value);
            },
        },
        open: {
            immediate: true,
            handler(isOpen) {
                if (isOpen) {
                    this.loadConfiguration();
                }
            },
        },
    },

    created() {
        this.podcastConfig = usePodcastConfiguration();
        this.syncResolvedConfigState();
        this.loadConfiguration();
    },

    methods: {
        syncResolvedConfigState() {
            const categoriesSource = this.podcastConfig?.categories;
            const optionsMetaSource = this.podcastConfig?.optionsMeta;
            const loadingSource = this.podcastConfig?.loading;
            const errorSource = this.podcastConfig?.error;

            let categories = [];
            if (Array.isArray(categoriesSource)) {
                categories = categoriesSource;
            } else if (Array.isArray(categoriesSource?.value)) {
                categories = categoriesSource.value;
            }

            let loading = false;
            if (typeof loadingSource === 'boolean') {
                loading = loadingSource;
            } else if (typeof loadingSource?.value === 'boolean') {
                loading = loadingSource.value;
            }

            let error = null;
            if (typeof errorSource === 'string') {
                error = errorSource;
            } else if (typeof errorSource?.value === 'string') {
                error = errorSource.value;
            }

            let optionsMeta = {};
            if (optionsMetaSource && typeof optionsMetaSource === 'object' && !Array.isArray(optionsMetaSource)) {
                optionsMeta = optionsMetaSource;
            } else if (
                optionsMetaSource?.value &&
                typeof optionsMetaSource.value === 'object' &&
                !Array.isArray(optionsMetaSource.value)
            ) {
                optionsMeta = optionsMetaSource.value;
            }

            this.resolvedConfigState = {
                categories,
                optionsMeta,
                loading,
                error,
            };
        },

        cloneSelections(value) {
            if (!value || typeof value !== 'object') {
                return {};
            }

            return Object.entries(value).reduce((carry, [categoryKey, subcategories]) => {
                if (!subcategories || typeof subcategories !== 'object') {
                    return carry;
                }

                carry[categoryKey] = Object.entries(subcategories).reduce((subCarry, [subcategoryKey, selected]) => {
                    if (Array.isArray(selected)) {
                        subCarry[subcategoryKey] = [...selected];
                    } else if (typeof selected === 'string') {
                        subCarry[subcategoryKey] = selected;
                    }

                    return subCarry;
                }, {});

                return carry;
            }, {});
        },

        normalizeFromModelValue(value) {
            const clonedSelections = this.cloneSelections(value?.selections ?? {});

            return {
                selections: this.normalizeVoiceCastInSelections(this.applyDefaultSelections(clonedSelections)),
            };
        },

        getVoiceCastSubcategory() {
            const voiceCategory = (this.categories || []).find(category => category.key === 'voice_cast');
            return (voiceCategory?.subcategories || []).find(subcategory => subcategory.key === 'voices') || null;
        },

        normalizeVoiceCastInSelections(selections) {
            const voiceSubcategory = this.getVoiceCastSubcategory();
            if (!voiceSubcategory) {
                return selections;
            }

            const normalized = this.cloneSelections(selections);
            const current = normalized?.voice_cast?.voices;
            const next = normalizeVoiceCastSelectionKeys(
                Array.isArray(current) ? current : [],
                voiceSubcategory.options || [],
                this.optionsMeta?.legacy_voice_keys || {}
            );

            normalized.voice_cast = {
                ...(normalized?.voice_cast || {}),
                voices: next,
            };

            return normalized;
        },

        getRecognizedSelectedVoiceKeys(categoryKey, subcategory) {
            const selected = this.getSelectedOption(categoryKey, subcategory.key);
            if (!Array.isArray(selected)) {
                return [];
            }

            return normalizeVoiceCastSelectionKeys(
                selected,
                subcategory.options || [],
                this.optionsMeta?.legacy_voice_keys || {}
            );
        },

        applyDefaultSelections(selections) {
            const normalized = this.cloneSelections(selections);

            (this.categories || []).forEach(category => {
                (category.subcategories || []).forEach(subcategory => {
                    const selectionMode = this.getSelectionMode(subcategory);
                    const current = normalized?.[category.key]?.[subcategory.key];
                    const defaultOptions = (subcategory.options || []).filter(option => option?.default === true);
                    if (defaultOptions.length === 0) {
                        return;
                    }

                    if (selectionMode === 'single') {
                        if (typeof current === 'string' && current.trim() !== '') {
                            return;
                        }

                        normalized[category.key] = {
                            ...(normalized?.[category.key] || {}),
                            [subcategory.key]: defaultOptions[0].key,
                        };

                        return;
                    }

                    const voiceSubcategory =
                        subcategory.key === 'voices' && category.key === 'voice_cast' ? subcategory : null;
                    let currentValues = [];
                    if (voiceSubcategory !== null) {
                        currentValues = this.getRecognizedSelectedVoiceKeys(category.key, voiceSubcategory);
                    } else if (Array.isArray(current)) {
                        currentValues = current.filter(value => typeof value === 'string' && value.trim() !== '');
                    }
                    if (currentValues.length > 0) {
                        return;
                    }

                    const maxSelections = this.getMaxSelections(subcategory);
                    const defaults = defaultOptions
                        .map(option => option.key)
                        .filter(key => typeof key === 'string' && key.trim() !== '')
                        .slice(0, maxSelections);

                    if (defaults.length === 0) {
                        return;
                    }

                    normalized[category.key] = {
                        ...(normalized?.[category.key] || {}),
                        [subcategory.key]: defaults,
                    };
                });
            });

            return normalized;
        },

        async loadConfiguration() {
            if (!this.podcastConfig?.load) return;
            this.syncResolvedConfigState();
            await this.podcastConfig.load();
            this.syncResolvedConfigState();
            this.localConfig = this.normalizeFromModelValue(this.modelValue);
        },

        getSelectedOption(categoryKey, subcategoryKey) {
            return this.localSelections?.[categoryKey]?.[subcategoryKey] ?? null;
        },

        getSelectionMode(subcategory) {
            return subcategory?.selection_mode === 'multiple' ? 'multiple' : 'single';
        },

        getMaxSelections(subcategory) {
            const max = Number(subcategory?.max_selections ?? 2);
            return Number.isFinite(max) && max > 0 ? max : 2;
        },

        isOptionSelected(categoryKey, subcategory, optionKey) {
            const selectionMode = this.getSelectionMode(subcategory);

            if (selectionMode === 'multiple') {
                const selected =
                    categoryKey === 'voice_cast' && subcategory.key === 'voices'
                        ? this.getRecognizedSelectedVoiceKeys(categoryKey, subcategory)
                        : this.getSelectedOption(categoryKey, subcategory.key);

                return Array.isArray(selected) && selected.includes(optionKey);
            }

            return this.getSelectedOption(categoryKey, subcategory.key) === optionKey;
        },

        isOptionDisabled(categoryKey, subcategory, optionKey) {
            if (this.getSelectionMode(subcategory) !== 'multiple') {
                return false;
            }

            const selected =
                categoryKey === 'voice_cast' && subcategory.key === 'voices'
                    ? this.getRecognizedSelectedVoiceKeys(categoryKey, subcategory)
                    : this.getSelectedOption(categoryKey, subcategory.key);
            const maxSelections = this.getMaxSelections(subcategory);

            if (!Array.isArray(selected)) {
                return false;
            }

            return selected.length >= maxSelections && !selected.includes(optionKey);
        },

        selectOption(categoryKey, subcategory, optionKey) {
            const subcategoryKey = subcategory.key;
            const selectionMode = this.getSelectionMode(subcategory);
            const existingCategorySelections = this.localSelections?.[categoryKey] || {};
            let nextSelection = optionKey;

            if (selectionMode === 'multiple') {
                const selected = this.getSelectedOption(categoryKey, subcategoryKey);
                let selectedList = [];
                if (subcategoryKey === 'voices' && categoryKey === 'voice_cast') {
                    selectedList = this.getRecognizedSelectedVoiceKeys(categoryKey, subcategory);
                } else if (Array.isArray(selected)) {
                    selectedList = selected.filter(value => typeof value === 'string' && value.trim() !== '');
                }
                const existingIndex = selectedList.indexOf(optionKey);

                if (existingIndex >= 0) {
                    selectedList.splice(existingIndex, 1);
                } else if (selectedList.length < this.getMaxSelections(subcategory)) {
                    selectedList.push(optionKey);
                } else {
                    return;
                }

                nextSelection = selectedList;
            }

            this.localConfig = {
                ...this.localConfig,
                selections: {
                    ...this.localSelections,
                    [categoryKey]: {
                        ...existingCategorySelections,
                        [subcategoryKey]: nextSelection,
                    },
                },
            };
            this.emitConfig();
        },
        handleSingleSelectChange(categoryKey, subcategory, event) {
            const optionKey = String(event?.target?.value || '');
            if (optionKey !== '') {
                this.selectOption(categoryKey, subcategory, optionKey);
            }
        },
        handleSingleSelectModel(categoryKey, subcategory, value) {
            const optionKey = String(value || '');
            if (optionKey !== '') {
                this.selectOption(categoryKey, subcategory, optionKey);
            }
        },

        resetSelections() {
            this.localConfig = {
                selections: this.normalizeVoiceCastInSelections(this.applyDefaultSelections({})),
            };
            this.emitConfig();
        },

        emitConfig() {
            this.$emit('update:modelValue', this.buildPayload());
        },

        buildPayload() {
            return {
                selections: this.localSelections,
            };
        },

        applyConfig() {
            const payload = this.buildPayload();
            this.$emit('update:modelValue', payload);
            this.$emit('apply', {
                config: payload,
                text: this.previewText,
            });
            this.$emit('update:open', false);
        },
    },
};
</script>

<style lang="scss">
.p-drawer-right .podcast-config-drawer.p-drawer {
    width: min(920px, 30vw);
    max-width: 30vw;
}

.b-sidebar.podcast-config-drawer .sidebar-content {
    width: min(920px, 30vw);
    max-width: 30vw;
    background: var(--p-content-surface-ground, #f3f4f6);
}

// Phones: the 30vw width leaves the drawer a cramped sliver. Use the full
// viewport so the config options and preview have room.
@media (max-width: 768px) {
    .p-drawer-right .podcast-config-drawer.p-drawer,
    .b-sidebar.podcast-config-drawer .sidebar-content {
        width: 100vw;
        max-width: 100vw;
    }
}

.podcast-drawer-shell {
    background: var(--p-content-surface-ground, #f3f4f6);
}

.podcast-option-matrix {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.625rem;
}

.podcast-config-drawer :deep(.p-select .p-select-label) {
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
}

.podcast-option-card {
    width: 100%;
    min-height: 82px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    text-align: left;
    border: 1px solid #e5e7eb;
    border-radius: 0.95rem;
    background: #fff;
    padding: 0.55rem 0.6rem;
    transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease, background-color 0.15s ease;
}

.podcast-option-card__label {
    font-size: 0.68rem;
    font-weight: 600;
    line-height: 1.1;
    margin: 0;
    word-break: break-word;
}

.podcast-option-card__subtitle {
    margin: 0.2rem 0 0;
    font-size: 0.6rem;
    line-height: 1.1;
    color: var(--p-text-muted-color, #6b7280);
}

.podcast-option-card:hover {
    border-color: #93c5fd;
    box-shadow: 0 6px 14px rgba(15, 23, 42, 0.08);
    background: #f8fbff;
    transform: translateY(-1px);
}

.podcast-option-card.is-disabled {
    cursor: not-allowed;
    opacity: 0.65;
}

.podcast-option-card.is-active {
    border-color: var(--p-primary-color, #3b82f6);
    box-shadow: 0 0 0 1px var(--p-primary-color, #3b82f6) inset;
    background: rgba(59, 130, 246, 0.04);
}

@media (max-width: 1280px) {
    .podcast-option-matrix {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}
</style>
